import { FluentBundle } from 'fluent';
import {
  GetString,
} from 'fluent-react';
import sortBy from 'lodash-es/sortBy';
import React from 'react';
import {
  Location,
  ProductClass,
  ProductLevel,
  ProductType,
  ProductYear,
  TreeMapInput,
  TreeMapProduct,
  TreeMapType,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { getCountryName } from '../../Utils';
import GlossaryTerm, { TooltipAlign, TooltipType } from '../landing/GlossaryTerm';
import Localized from '../landing/Localized';
import {
  ContentContainer,
  HighlightedText,
  IntroText,
  Title,
} from './SharedStyling';
import Slide from './Slide';
import SlideAdvancer, {Direction} from './SlideAdvancer';

const query = gql`
  query ExportComplexitySideBar(
    $facet: TreeMapType!,
    $productClass: ProductClass!,
    $productLevel: ProductLevel!,
    $locationLevel: LocationLevel,
    $product: ID,
    $partner: ID,
    $locationId: ID!,
    $year: Int!,
  ) {
    treeMap(
      facet: $facet,
      productClass: $productClass,
      year: $year,
      productLevel: $productLevel,
      locationLevel: $locationLevel,
      location: $locationId,
      product: $product,
      partner: $partner,
      mergePci: false,
    ) {
      ... on TreeMapProduct {
        product {
          id
          productName: shortName
          type: productType
          topLevelParent {
            id
            shortName
          }
        }
        exportValue
      }
    }
    allProductYear(year: $year, productClass: $productClass, productLevel: $productLevel) {
      product {
        id
      }
      complexityLevel
    }
    location(id: $locationId) {
      id
      locationName: shortName
      nameAbbr
      thePrefix
    }
  }
`;

interface ProductDatum {
  exportValue: TreeMapProduct['exportValue'];
  product: {
    id: TreeMapProduct['product']['id']
    type: TreeMapProduct['product']['productType']
    productName: TreeMapProduct['product']['shortName'],
    topLevelParent: {
      id: TreeMapProduct['product']['topLevelParent']['id'];
    };
  };
}
interface ProductYearDatum {
  product: {
    id: ProductYear['product']['id'],
  };
  complexityLevel: ProductYear['complexityLevel'];
}
interface SuccessResponse {
  treeMap: ProductDatum[];
  allProductYear: ProductYearDatum[];
  location: null | {
    id: Location['id']
    locationName: Location['shortName'],
    nameAbbr: Location['nameAbbr'],
    thePrefix: Location['thePrefix'],
  };
}

interface Variables extends TreeMapInput {
  locationId: string;
  year: number;
}

interface Props {
  onNextSlideClick: () => void;
  onPrevSlideClick: () => void;
  getFluentString: GetString;
  countryId: string;
  year: number;
  fluentBundle: FluentBundle;
  isHelpActive: boolean;
  helpTooltipIndex: number;
}

export default (props: Props) => {
  const {
    onNextSlideClick, onPrevSlideClick, getFluentString,
    countryId, year, fluentBundle, isHelpActive, helpTooltipIndex,
  } = props;

  const yearAsString = year.toString();

  const variables: Variables = {
    facet: TreeMapType.CPY_C,
    productClass: ProductClass.HS,
    year,
    productLevel: ProductLevel.section,
    locationLevel: null,
    location: countryId,
    product: null, partner: null,
    locationId: countryId,
  };

  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});
  let output: React.ReactElement<any> | null;
  if (loading === true) {
    output = null;
  } else if (error !== undefined) {
    console.error(error);
    output = null;
  } else if (data !== undefined) {
    const {location, treeMap, allProductYear} = data;
    if (location === null) {
      output = null;
    } else {
      const nonServices = treeMap.filter(({product: {type, topLevelParent}}) => {
        // filter out non goods and "Other"
        return (type === ProductType.Goods && topLevelParent.id !== 'product-HS-10');
      });
      const sorted = sortBy(nonServices, ({exportValue}) => exportValue).reverse();
      const [highestExportSectorDatum, secondHighestExportSectorDatum] = sorted;
      const highestExportSectorDatumYear =
        allProductYear.find(d => d.product.id === highestExportSectorDatum.product.id);
      const secondHighestExportSectorDatumYear =
        allProductYear.find(d => d.product.id === secondHighestExportSectorDatum.product.id);
      const highestExportSector = highestExportSectorDatum.product.productName;
      const highestExportComplexity =
        highestExportSectorDatumYear ? highestExportSectorDatumYear.complexityLevel : null;
      const secondHighestExportSector = secondHighestExportSectorDatum.product.productName;
      const secondHighestExportComplexity =
        secondHighestExportSectorDatumYear ? secondHighestExportSectorDatumYear.complexityLevel : null;
      const {locationName, nameAbbr, thePrefix} = location;
      const para1Args = {
        knowhowGlossaryTerm: (
          <GlossaryTerm
            type={TooltipType.Standard}
            term={getFluentString('glossary-knowhow-term-name').toLowerCase()}
            explanation={getFluentString('glossary-knowhow-term-definition')}
            citation={undefined}
            align={TooltipAlign.Standard}
            attachment={null}
          />
        ),
      };
      const {namePrimary} = getCountryName({shortName: locationName, nameAbbr, thePrefix: Boolean(thePrefix)});
      const para2Args = {
        'country-name': namePrimary,
        'highest-export-complexity': highestExportComplexity,
        'second-export-complexity': secondHighestExportComplexity,
        'complexity-is-same': `${(highestExportComplexity === secondHighestExportComplexity)}`,
        'largestExportCategoryName': (
          <HighlightedText
            children={highestExportSector}
          />
        ),
        'secondLargestExportCategoryName': (
          <HighlightedText
            children={secondHighestExportSector}
          />
        ),
      };
      output = (
        <>
          <Localized
            bundle={fluentBundle}
            messageId='export-complexity-intro-text-para-1'
            messageArgs={para1Args}
          >
            <IntroText />
          </Localized>
          <Localized
            bundle={fluentBundle}
            messageId='export-complexity-intro-text-para-2'
            messageArgs={para2Args}
          >
            <IntroText />
          </Localized>
        </>
      );
    }
  } else {
    output = null;
  }

  return (
    <Slide>
      <SlideAdvancer
        onClick={onPrevSlideClick}
        direction={Direction.Previous}
        isHelpActive={isHelpActive}
        helpTooltipIndex={helpTooltipIndex}
        getFluentString={getFluentString}
      >
        {getFluentString('section-nav-slide-export-basket', {year: yearAsString})}
      </SlideAdvancer>
      <ContentContainer>
        <Title>{getFluentString('section-nav-slide-export-complexity', {year: yearAsString})}</Title>
        {output}
      </ContentContainer>
      <SlideAdvancer
        onClick={onNextSlideClick}
        direction={Direction.Next}
        isHelpActive={isHelpActive}
        helpTooltipIndex={helpTooltipIndex}
        getFluentString={getFluentString}
      >
        {getFluentString('section-nav-slide-export-growth-dynamics')}
      </SlideAdvancer>
    </Slide>

  );
};
